import { useTranslation } from 'react-i18next'
import {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useRouter } from 'next/router'
import { FormikProps } from 'formik'

import { Brands } from '../../rest/types/Brand'
import { Categories } from '../../rest/types/Categories'
import { actions, selectors } from '../../redux'
import { transformFilters } from '../../helpers/OfferHelpers'
import { SortingFiltersProps } from '../../components/SortingFilters'

export enum Sort {
  Default = '1',
  LowestPrice = '2',
  HigherPrice = '3',
  Feedback = '4',
}

const useSortingFilters = ({
  categories,
  brands,
  search,
  total,
  hasTrackedRef,
}: {
  categories?: Categories
  brands?: Brands
  search?: string
  total: number
  hasTrackedRef: MutableRefObject<boolean>
}): {
  props: SortingFiltersProps
} => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false)
  const filters = useSelector(selectors.filters.filters)
  const router = useRouter()

  const handleOrderInputChange = useCallback(
    (e: any) =>
      dispatch(actions.filters.setSort({ sort: e.target.value as Sort })),
    [dispatch]
  )

  const handleFilterButtonClick = useCallback(
    () => setIsFilterOpen(!isFilterOpen),
    [isFilterOpen]
  )

  const formRef = useRef<FormikProps<any>>(null)

  useEffect(() => {
    formRef.current?.setValues(filters)
  }, [filters])

  const filtersProps: SortingFiltersProps = useMemo(
    () => ({
      ...(search && {
        search: {
          text: t('filtersSearchText', { search }),
          results: t('filtersSearchResults', { results: total }),
        },
      }),
      filterButton: {
        text: t('sort_text_button'),
        onClick: handleFilterButtonClick,
      },
      orderInput: {
        name: 'order',
        onChange: handleOrderInputChange,
        options: [
          {
            label: t('sort_label_best_result'),
            value: Sort.Default,
          },
          {
            label: t('sort_label_rising_price'),
            value: Sort.LowestPrice,
          },
          {
            label: t('sort_label_decreasing_price'),
            value: Sort.HigherPrice,
          },
          {
            label: t('sort_label_feedback'),
            value: Sort.Feedback,
          },
        ],
      },
      filters: {
        texts: {
          categoriesTitle: t('products_filters_categories_title'),
          brandsTitle: t('products_filters_brands_title'),
          offersTitle: t('products_filters_offers_title'),
          submitButton: t('products_filters_offers_submit_button'),
        },
        form: {
          formikForm: {
            innerRef: formRef,
            initialValues: {
              groupPurchase: filters.groupPurchase || [],
              brand: filters.brand || [],
              categories: filters.categories || [],
            },
            onSubmit: (values) => {
              dispatch(
                actions.filters.setFilters({
                  filters: transformFilters(values),
                })
              )

              const baseUrl = router.asPath.split('?').shift()

              dispatch(
                actions.filters.redirect({
                  url: baseUrl ?? '',
                })
              )

              hasTrackedRef.current = false
            },
          },
          fields: {
            offers: {
              name: 'groupPurchase',
              options: [
                {
                  label: t('products_filters_offers_field_catalogue'),
                  value: 'false',
                },
                {
                  label: t('products_filters_offers_field_group_purchase'),
                  value: 'true',
                },
              ],
            },
            ...(brands && {
              brands: {
                name: 'brand',
                placeholder: t('products_filters_brands_title'),
                isSearchable: true,
                isMulti: true,
                options: brands.map((brand) => ({
                  label: brand.name ?? '',
                  value: brand.id,
                })),
              },
            }),
            ...(categories && {
              categories: {
                name: 'categories',
                label: t('products_filters_categories_field_label'),
                placeholder: t('products_filters_categories_field_placeholder'),
                isSearchable: true,
                isMulti: true,
                options: categories.map((cat) => ({
                  label: cat.name,
                  value: cat.id,
                })),
              },
            }),
          },
        },
        isOpen: isFilterOpen,
      },
    }),
    [
      search,
      t,
      total,
      handleFilterButtonClick,
      handleOrderInputChange,
      filters.groupPurchase,
      filters.brand,
      filters.categories,
      brands,
      categories,
      isFilterOpen,
      dispatch,
      router.asPath,
      hasTrackedRef,
    ]
  )

  return useMemo(
    () => ({
      props: filtersProps,
    }),
    [filtersProps]
  )
}

export default useSortingFilters
